import React, { useMemo } from "react";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useDevice, useFormatNumericHook } from "hooks";
import { useNumberFormatSelector, useReportsQueriesSelector } from "store";
import { CashflowGraph, CurrencyType, ReportCashflowAum } from "store/types";

import { currencySign } from "../../../constants";

interface IProps {
  data: CashflowGraph | undefined;
  chartRef: React.RefObject<HighchartsReact.RefObject> | null;
  aumData: ReportCashflowAum | null;
  currency?: CurrencyType;
}

export const CashflowChartComponent: React.FC<IProps> = ({ data, chartRef, aumData, currency }) => {
  const { abbreviated, decimal } = useNumberFormatSelector();
  const formatNumeric = useFormatNumericHook();
  const { isMobile } = useDevice();

  const { currency: currencyFromStore } = useReportsQueriesSelector();

  const curSymbol = useMemo(() => {
    return currencySign[currency || currencyFromStore];
  }, [currency, currencyFromStore]);

  const graphOptions: Highcharts.Options | undefined = useMemo(() => {
    if (data) {
      const totalYears = 11; // 5 years past + current + 5 years future
      const yearWidth = 180; // Approximate width per year in pixels

      return {
        ...options,
        chart: {
          ...options.chart,
          ...(isMobile
            ? {
                scrollablePlotArea: {
                  minWidth: totalYears * yearWidth,
                  scrollPositionX: 0
                }
                // Don't set width on mobile - let it fill container, scrollablePlotArea handles scrolling
              }
            : {
                width: undefined // Let chart fill container width on desktop
              })
        },
        tooltip: {
          ...options.tooltip,
          formatter: function () {
            let tooltip = `<b>${this.x}</b><br/>`;
            if (this.points) {
              this.points.forEach((point) => {
                const value =
                  point.y !== null && point.y !== undefined
                    ? formatNumeric({ value: curSymbol + point.y, format: { abbreviated, decimal } })
                    : "N/A";
                tooltip += `${point.series.name}: <b>${value}</b><br/>`;
              });
            }
            return tooltip;
          }
        },
        yAxis: {
          ...options.yAxis,
          labels: {
            enabled: false
          }
        },
        legend: {
          ...options.legend,
          ...(isMobile && {
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemWidth: undefined,
            maxHeight: undefined,
            itemMarginBottom: 0,
            itemDistance: 15,
            itemStyle: {
              fontSize: "11px"
            },
            width: undefined
          })
        },
        series: [
          {
            type: "column",
            name: "Unfunded",
            data: data.map((d) => d[0]),
            dataLabels: { enabled: false }
          },
          {
            type: "column",
            name: "Paid-in Capital",
            data: data.map((d) => d[2]),
            dataLabels: { enabled: false }
          },
          {
            type: "column",
            name: "Distribution",
            data: data.map((d) => d[3]),
            dataLabels: { enabled: false }
          },
          {
            type: "spline",
            name: "Valuation",
            data: data.map((d) => d[1]),
            marker: {
              symbol: "circle",
              fillColor: "#D9D9D9",
              lineWidth: 2,
              lineColor: "#26A6AE"
            },
            dataLabels: {
              enabled: !!aumData?.aum_enabled && !!aumData?.total_aum,
              backgroundColor: "#496682",
              y: -10,
              style: {
                color: "#ffffff",
                textOutline: "none",
                fontSize: "10px"
              },
              borderRadius: 4,
              padding: 4,
              allowOverlap: true,
              distance: 40,
              staggerLines: 2,
              overflow: "allow",
              crop: false,
              formatter: function () {
                return aumData?.aum_enabled && aumData?.total_aum && this.y
                  ? `${formatNumeric({ value: ((this.y / aumData.total_aum) * 100).toFixed(2), format: { abbreviated, decimal } })}%`
                  : "0%";
              }
            }
          },
          {
            type: "line" as const,
            name: "Allocation",
            data: data.map((d) => (d[4] ? d[4] : null)),
            dashStyle: "LongDash" as Highcharts.DashStyleValue,
            marker: {
              enabled: false
            }
          }
        ]
      };
    }
  }, [data, aumData, abbreviated, decimal, curSymbol, formatNumeric]);

  return (
    <HighchartsReact
      ref={chartRef}
      containerProps={{
        style: {
          height: "350px",
          maxHeight: "350px",
          width: isMobile ? "auto" : "100%",
          overflow: isMobile ? "visible" : "hidden",
          border: "none"
        }
      }}
      highcharts={Highcharts}
      options={graphOptions}
    />
  );
};

const graphColors: string[] = [
  "#92A7BC", // Unfunded Commitment
  "#071F35", // Capital Call
  "#C09560", // Distribution
  "#26A6AE", // Valuation
  "#399FFD" // Allocation
];

const options: Highcharts.Options = {
  chart: {
    type: "container",
    height: 350,
    width: undefined, // Fill container width
    backgroundColor: "#ffffff",
    plotBackgroundColor: "#ffffff",
    marginRight: 0,
    marginLeft: 0
  },
  title: {
    text: undefined
  },
  exporting: { enabled: false },
  tooltip: {
    shared: true
  },
  legend: {
    layout: "horizontal",
    align: "center",
    verticalAlign: "bottom",
    itemStyle: {
      fontSize: "14px"
    },
    itemWidth: undefined,
    maxHeight: undefined,
    itemMarginBottom: 0
  },
  xAxis: {
    title: { text: "Years" },
    // lineColor: "#dbe8f5",
    // lineWidth: 1,
    // tickColor: "#dbe8f5",
    plotBands: [
      {
        from: new Date().getFullYear() + 0.5,
        to: new Date().getFullYear() + 21,
        color: "rgba(255, 255, 255, 0.45)",
        zIndex: 7
      }
    ],
    plotLines: [
      // {
      //   value: new Date().getFullYear() - 5,
      //   color: "#dbe8f5",
      //   width: 1,
      //   zIndex: 5
      // },
      {
        value: new Date().getFullYear() + 0.4,
        color: "#496682",
        width: 3,
        dashStyle: "LongDash"
      }
    ]
  },
  plotOptions: {
    series: {
      pointStart: new Date().getFullYear() - 5
    }
  },
  yAxis: {
    title: { text: "" }
  },
  series: [{ data: [], type: "column" }],
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          legend: {
            layout: "horizontal",
            align: "center",
            verticalAlign: "bottom",
            itemWidth: undefined,
            maxHeight: undefined,
            itemMarginBottom: 0,
            itemDistance: 15,
            itemStyle: {
              fontSize: "11px"
            },
            width: undefined
          }
        }
      }
    ]
  },
  colors: graphColors,
  credits: { enabled: false }
};
